import React, { useState } from 'react';
import { getSmoothStepPath, EdgeLabelRenderer } from 'reactflow';
import useStore from '../store/useStore';

function getFieldFromHandle(node, handleId) {
  if (!handleId || !node?.data?.fields) return null;
  const match = handleId.match(/^field-(\d+)/);
  if (!match) return null;
  return node.data.fields[parseInt(match[1], 10)] || null;
}

export default function RelationshipEdge({
  id, source, target,
  sourceX, sourceY, targetX, targetY,
  sourcePosition, targetPosition, data, selected,
  sourceHandleId, targetHandleId,
}) {
  const [hovered, setHovered] = useState(false);
  const nodes = useStore((s) => s.nodes);
  const relationHoverMode = useStore((s) => s.relationHoverMode);

  const sourceType = data?.sourceType || 'One';
  const targetType = data?.targetType || 'One';
  const isEnum = sourceType === 'Enum' || targetType === 'Enum';

  const srcNode = nodes.find((n) => n.id === source);
  const tgtNode = nodes.find((n) => n.id === target);
  const srcName = srcNode?.data?.name || 'Source';
  const tgtName = tgtNode?.data?.name || 'Target';

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX, sourceY, targetX, targetY,
    sourcePosition, targetPosition,
    borderRadius: 8,
  });

  const srcDir = sourcePosition === 'left' ? -1 : 1;
  const tgtDir = targetPosition === 'left' ? -1 : 1;
  const isActive = selected || hovered;
  const strokeColor = isActive ? 'var(--accent)' : 'var(--edge-color)';
  const strokeW = isActive ? 2.5 : 1.8;

  const renderMarker = (x, y, dir, type) => {
    if (type === 'Many') {
      return (
        <g>
          <line x1={x} y1={y} x2={x + 14 * dir} y2={y} stroke={strokeColor} strokeWidth={strokeW} />
          <line x1={x} y1={y} x2={x + 14 * dir} y2={y - 8} stroke={strokeColor} strokeWidth={strokeW} />
          <line x1={x} y1={y} x2={x + 14 * dir} y2={y + 8} stroke={strokeColor} strokeWidth={strokeW} />
        </g>
      );
    }
    return <line x1={x + 4 * dir} y1={y - 8} x2={x + 4 * dir} y2={y + 8} stroke={strokeColor} strokeWidth={strokeW} />;
  };

  // Determine owner and field name from handle
  const isManyToOne = sourceType === 'Many' || targetType === 'Many';
  const srcIsOwner = isManyToOne ? sourceType === 'Many' : true;
  const ownerNode = srcIsOwner ? srcNode : tgtNode;
  const ownerName = srcIsOwner ? srcName : tgtName;
  const refName = srcIsOwner ? tgtName : srcName;
  const ownerHandle = srcIsOwner ? sourceHandleId : targetHandleId;
  const ownerField = getFieldFromHandle(ownerNode, ownerHandle);
  const annotation = isManyToOne ? '@ManyToOne' : '@OneToOne';
  const fieldType = refName;
  const fieldName = ownerField?.name || refName.charAt(0).toLowerCase() + refName.slice(1);

  return (
    <>
      <g>
        <path d={edgePath} fill="none" stroke="transparent" strokeWidth={16}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{ cursor: 'pointer' }}
        />
        <path
          id={id} className="react-flow__edge-path" d={edgePath}
          fill="none" stroke={strokeColor} strokeWidth={strokeW}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        />
        {!isEnum && (
          <>
            {renderMarker(sourceX, sourceY, srcDir, sourceType)}
            {renderMarker(targetX, targetY, tgtDir, targetType)}
          </>
        )}
      </g>
      {!isEnum && hovered && (
        <EdgeLabelRenderer>
          <div
            className="edge-code-tooltip"
            style={{
              position: 'absolute',
              left: labelX,
              top: labelY,
              transform: 'translate(-50%, -100%) translateY(-12px)',
              pointerEvents: 'none',
            }}
          >
            {relationHoverMode === 'json' ? (
              <>
                <div className="edge-code-line edge-code-class">
                  <span className="edge-code-comment">// {ownerName} response</span>
                </div>
                <div className="edge-code-line">{'{'}</div>
                <div className="edge-code-line edge-code-dots">...</div>
                <div className="edge-code-line">
                  <span className="edge-code-key">"{fieldName}"</span>: {'{'}
                </div>
                <div className="edge-code-line edge-code-dots edge-code-indent">...</div>
                <div className="edge-code-line">{'},'}</div>
                <div className="edge-code-line edge-code-dots">...</div>
                <div className="edge-code-line">{'}'}</div>
              </>
            ) : (
              <>
                <div className="edge-code-line edge-code-class">
                  <span className="edge-code-kw">public class</span> {ownerName} {'{'}
                </div>
                <div className="edge-code-line edge-code-dots">...</div>
                <div className="edge-code-line">
                  <span className="edge-code-annotation">{annotation}</span>
                </div>
                <div className="edge-code-line">
                  <span className="edge-code-type">{fieldType}</span> {fieldName};
                </div>
                <div className="edge-code-line edge-code-dots">...</div>
                <div className="edge-code-line">{'}'}</div>
              </>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
